<template>
    <div class="show-detail-chart">
        <div v-if="chartData.length === 0" class="chart-placeholder"></div>
        <div v-else>
            <div class="chart-view-multiple-tabs">
                <ul class="chart-view-tabs">
                    <li
                        v-for="(option, index) in dataViews"
                        :key="option.label"
                        :class="{ current: index === currentViewIndex }"
                        @click.prevent="currentViewIndex = index"
                    >
                        {{ option.label }}
                    </li>
                </ul>

                <ul class="chart-view-tabs">
                    <li
                        v-for="(option, index) in dateRanges"
                        :key="option.label"
                        :class="{ current: index === currentDateIndex }"
                        @click.prevent="updateDateRange(index)"
                    >
                        {{ option.label }}
                    </li>
                </ul>
            </div>

            <line-chart
                :display-x-grid="false"
                :chart-data="chartData"
                :annotation="annotation"
                :plugins="plugins"
                :animate="true"
            />
        </div>
    </div>
</template>

<script>
import sortBy from 'lodash/sortBy';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import tonyDates from 'src/config/tony-dates';
import isBefore from 'date-fns/is_before';
import isEqual from 'date-fns/is_equal';
import differenceInCalendarWeeks from 'date-fns/difference_in_calendar_weeks';
import { shortenNumber, formatDate, getNextSunday } from 'src/util/helpers';

export default {
    data() {
        return {
            currentDateIndex: 4,
            currentViewIndex: 0,
            dataViews: [
                {
                    label: 'Gross',
                    potentialLabel: 'Potential Gross',
                    filter: 'money',
                    view: 'gross',
                },
                {
                    label: 'Attendance',
                    potentialLabel: 'Capacity',
                    view: 'attendance',
                    filter: 'wholeCommaNumber',
                },
            ],
        };
    },
    mounted() {
        this.$store.dispatch(
            'grosses/fetchChartForShow',
            this.$route.params.show,
        );
    },
    // watch: {
    // grosses: function(val) {
    // "All" should never be the default option
    // if (this.dateRanges.length - 1 === this.currentDateIndex) {
    //     this.currentDateIndex = this.currentDateIndex - 1;
    // }
    // },
    // },
    computed: {
        show() {
            return this.$store.getters['shows/show'](this.$route.params.show);
        },
        grosses() {
            if (!this.show.id) {
                return [];
            }

            return this.$store.getters['grosses/chartForShow'](
                this.$route.params.show,
            );
        },

        currentView() {
            return this.dataViews[this.currentViewIndex];
        },
        dateRanges() {
            const firstWeek = this.orderedData[0];

            if (!firstWeek) {
                return [];
            }

            // Weeks since with a buffer of 4 to include things like 11 weeks of data for 12 week view
            const weeksSince =
                Math.max(
                    differenceInCalendarWeeks(new Date(), firstWeek.date),
                    4,
                ) + 4;

            return [
                {
                    label: '4w',
                    weeks: 4,
                },
                {
                    label: '12w',
                    weeks: 12,
                },
                {
                    label: '26w',
                    weeks: 26,
                },
                {
                    label: '52w',
                    weeks: 52,
                },
                {
                    label: '5Y',
                    weeks: 52 * 5,
                },
                {
                    label: 'All',
                    weeks: this.orderedData.length,
                },
            ]
                .filter(range => range.weeks <= weeksSince)
                .filter(range => {
                    if (!this.hasAggregate) {
                        return range.label !== 'All';
                    }

                    return true;
                });
        },
        chartWeeks() {
            if (this.dateRanges.length === 0) {
                return 0;
            }

            if (this.currentDateIndex > this.dateRanges.length - 1) {
                this.currentDateIndex = this.dateRanges.length - 1;
            }

            return this.dateRanges[this.currentDateIndex].weeks;
        },
        orderedData() {
            return sortBy(this.grosses, gross => gross.date);
        },
        chartData() {
            return this.orderedData
                .map(data => ({
                    x: this.getXValues(data),
                    y: Vue.filter('date')(data.date),
                }))
                .slice(this.chartWeeks * -1);
        },
        annotation() {
            if (!this.show.opening_date) {
                return null;
            }

            const annotations = [];

            const showTonyDate = find(
                tonyDates,
                tonys =>
                    isBefore(this.show.opening_date, tonys.cutoff) ||
                    isEqual(this.show.opening_date, tonys.cutoff),
            );

            if (showTonyDate) {
                annotations.push(
                    this.getAnnotationConfig({
                        label: 'Tonys',
                        date: showTonyDate.show,
                        order: 3,
                    }),
                );

                if (showTonyDate.nominationAnnouncements) {
                    annotations.push(
                        this.getAnnotationConfig({
                            label: 'Tony Nominations',
                            date: getNextSunday(
                                showTonyDate.nominationAnnouncements,
                            ),
                            order: 2,
                        }),
                    );
                }
            }

            annotations.push(
                this.getAnnotationConfig({
                    label: 'Opening Week',
                    date: getNextSunday(this.show.opening_date),
                    order: 1,
                }),
            );

            annotations.push(
                this.getAnnotationConfig({
                    label: 'Stagehand Strike',
                    date: 'November 18, 2007',
                    order: 4,
                }),
            );

            annotations.push(
                this.getAnnotationConfig({
                    label: 'COVID-19 Shutdown',
                    date: 'March 8, 2020',
                    // date: 'March 12, 2020',
                    order: 1,
                }),
            );

            annotations.push(
                this.getAnnotationConfig({
                    label: 'Re-opening',
                    date: 'August 8, 2021',
                    order: 2,
                }),
            );

            return {
                annotations,
            };
        },
        plugins() {
            return [
                {
                    beforeInit: chart => {
                        chart.config.options.tooltips.callbacks = {
                            beforeLabel: (tooltipItem, data) => {
                                const config = this.chartData[tooltipItem.index]
                                    .x[tooltipItem.datasetIndex];

                                const br =
                                    tooltipItem.datasetIndex > 0 ? '\n' : '';

                                return br + config.label + ':';
                            },
                            label: (tooltipItem, data) => {
                                const config = this.chartData[tooltipItem.index]
                                    .x[tooltipItem.datasetIndex];

                                if (config.filter) {
                                    return Vue.filter(config.filter)(
                                        tooltipItem.yLabel,
                                    );
                                }

                                return tooltipItem.yLabel;
                            },
                            title: (tooltipItem, data) => {
                                const config = this.chartData[
                                    tooltipItem[0].index
                                ].x[0];

                                return `${tooltipItem[0].xLabel} (#${config.number})`;
                            },
                        };
                    },
                },
                {
                    beforeInit: chart => {
                        chart.config.options.scales.yAxes[0].ticks.callback = (
                            value,
                            index,
                            values,
                        ) => {
                            return shortenNumber(
                                value,
                                this.currentView.view === 'gross' ? '$' : '',
                            );
                        };
                    },
                },
            ];
        },
        hasAggregate() {
            if (!this.show.id) {
                return false;
            }

            return this.$store.getters['shows/hasAggregate'](
                this.show.slug,
                'opening',
            );
        },
    },
    methods: {
        getAnnotationConfig({ label, date, order }) {
            return {
                drawTime: 'afterDatasetsDraw',
                type: 'line',
                mode: 'vertical',
                scaleID: 'x-axis-0',
                value: formatDate(date, 'M/D/YYYY'),
                borderColor: 'rgba(255, 255, 255, .5)',
                borderWidth: 1,
                borderDash: [4, 2],
                label: {
                    backgroundColor: 'rgba(255, 255, 255, 0)',
                    fontColor: 'rgba(255, 255, 255, .5)',
                    content: label,
                    enabled: true,
                    xAdjust: this.annotationXAdjust(label, date),
                    yAdjust: 10 + (order - 1) * 20,
                    position: 'top',
                    cornerRadius: 0,
                    xPadding: 0,
                    yPadding: 0,
                },
            };
        },
        annotationXAdjust(label, date) {
            const width = this.getTextWidth(label);

            const dateIndex = findIndex(this.chartData, data =>
                isEqual(data.y, date),
            );

            const adjustment = width / 2 + 5;

            if (dateIndex === -1) {
                return adjustment;
            }

            return dateIndex / this.chartData.length >= 0.8
                ? adjustment
                : -adjustment;
        },
        getTextWidth(label) {
            if (!this.textCtx) {
                const canvas = document.getElementById(
                    'canvas-text-width-helper',
                );
                this.textCtx = canvas.getContext('2d');
                this.textCtx.font = 'bold 12px sans-serif';
            }

            return this.textCtx.measureText(label).width;
        },
        updateDateRange(index) {
            this.currentDateIndex = index;
        },
        getXValues(dataSet) {
            const data = [
                {
                    label: this.currentView.label,
                    value: NaN,
                    filter: this.currentView.filter,
                    number: dataSet.week_number,
                },
                {
                    label: this.currentView.potentialLabel,
                    value: NaN,
                    filter: this.currentView.filter,
                    number: dataSet.week_number,
                },
            ];

            const subView = dataSet[this.currentView.view];

            if (subView.total === 0) {
                return data;
            }

            data[0].value = subView.total;

            if (subView.potential) {
                data[1].value = subView.potential;
            }

            if (this.currentView.view !== 'gross') {
                return data;
            }

            data[0].value = data[0].value / 100;

            if (subView.potential) {
                data[1].value = data[1].value / 100;
            }

            return data;
        },
    },
};
</script>
